import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchTailoredCVs,
  fetchResolvedCV,
  fetchCVOverrides,
  createTailoredCV,
  createGeneratedTailoredCV,
  updateCVOverrides,
  updateCV,
  enhanceTailoredCV,
  duplicateTailoredCV,
  deleteTailoredCV,
  createOrUpdateCvHandoffDraft,
  updateCvHandoff,
  sendCvHandoff,
  claimCvHandoff,
  fetchCvHandoffInvite,
  cancelCvHandoff,
  type TailoredCV,
  type TailoredCVOverrides,
  type ResolvedTailoredCV,
  type UpdateCVInput,
  type CvHandoffClaimIntent,
  type CvHandoffInput,
  type CvHandoffInvitePreview,
  type CvHandoffSendInput,
} from "../lib/tailored-cv-api";
import { QUERY_KEYS } from "../lib/query-keys";
import { invalidateCvDocumentQueries } from "../lib/cv-query-invalidation";
import { cvWriteArgs } from "../lib/cv-write-coordinator";
import { describeCvUpdateWrite, describeOverridesWrite } from "../lib/cv-write-policy";
import { useCvRevisionTracking, useCvWriteRunner } from "./useCvWriteRunner";

const CVS_KEY = ["cvs"] as const;
const cvKey = (id: string) => ["cv", id] as const;
const resolvedKey = (id: string) => ["cv", id, "resolved"] as const;
const overridesKey = (id: string) => ["cv", id, "overrides"] as const;

// ─── List all CVs ─────────────────────────────────────────────

export function useTailoredCVs(enabled = true) {
  return useQuery<TailoredCV[]>({
    queryKey: CVS_KEY,
    queryFn: fetchTailoredCVs,
    enabled,
    staleTime: 30_000,
  });
}

// ─── Get resolved (merged) tailored CV ────────────────────────

export function useResolvedCV(cvId: string, enabled = true) {
  // The revision of this read is the base the editor's saves state (B02).
  useCvRevisionTracking();
  return useQuery<ResolvedTailoredCV>({
    queryKey: resolvedKey(cvId),
    queryFn: () => fetchResolvedCV(cvId),
    enabled: enabled && !!cvId,
    staleTime: 15_000,
  });
}

// ─── Get raw overrides ────────────────────────────────────────

export function useCVOverrides(cvId: string, enabled = true) {
  useCvRevisionTracking();
  return useQuery<TailoredCVOverrides>({
    queryKey: overridesKey(cvId),
    queryFn: () => fetchCVOverrides(cvId),
    enabled: enabled && !!cvId,
    staleTime: 15_000,
  });
}

// ─── Create tailored CV ───────────────────────────────────────

export function useCreateTailoredCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTailoredCV,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CVS_KEY }),
  });
}

export function useCreateGeneratedTailoredCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createGeneratedTailoredCV,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CVS_KEY }),
  });
}

// ─── Update CV overrides (section selections, skills, etc.) ───

export function useUpdateCVOverrides(cvId: string) {
  const queryClient = useQueryClient();
  const runCvWrite = useCvWriteRunner(cvId);
  return useMutation({
    // Overrides are persisted as one merged document. The write coordinator
    // (B02) runs every save to this CV in call order, each stating the
    // revision the previous one returned (If-Match), so independent fields
    // never overwrite each other out of order and never self-conflict; a
    // newer pending style/section-order save replaces an unsent older one.
    mutationFn: (patch: Partial<Omit<TailoredCVOverrides, "version">>) =>
      runCvWrite(describeOverridesWrite(patch), (write) =>
        updateCVOverrides(cvId, patch, ...cvWriteArgs(write)),
      ),
    onSuccess: () => {
      void invalidateCvDocumentQueries(queryClient, cvId);
    },
  });
}

// ─── Update CV metadata (title, summary) ──────────────────────

export function useUpdateCV(cvId: string) {
  const queryClient = useQueryClient();
  const runCvWrite = useCvWriteRunner(cvId);
  return useMutation({
    mutationFn: (patch: UpdateCVInput) =>
      runCvWrite(describeCvUpdateWrite(patch), (write) =>
        updateCV(cvId, patch, ...cvWriteArgs(write)),
      ),
    onSuccess: () => {
      void invalidateCvDocumentQueries(queryClient, cvId);
    },
  });
}

// ─── Enhance tailored CV (in-place AI rewrite) ────────────────

export function useEnhanceTailoredCv(cvId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => enhanceTailoredCV(cvId),
    onSuccess: () => {
      void invalidateCvDocumentQueries(queryClient, cvId);
    },
  });
}

// ─── Duplicate tailored CV ─────────────────────────────────────

export function useDuplicateTailoredCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: duplicateTailoredCV,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
    },
  });
}

// ─── Delete tailored CV ───────────────────────────────────────

export function useDeleteTailoredCV() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteTailoredCV,
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: ["cv", id] });
      queryClient.removeQueries({ queryKey: ["profile", id] });
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.JOB_CATALOG_AUTO_APPLY,
      });
    },
  });
}

// ─── Create CV for someone else handoff ───────────────────────

export function useCreateOrUpdateCvHandoffDraft(cvId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CvHandoffInput) =>
      createOrUpdateCvHandoffDraft(cvId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({ queryKey: cvKey(cvId) });
    },
  });
}

export function useUpdateCvHandoff(cvId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CvHandoffInput) => updateCvHandoff(cvId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({ queryKey: cvKey(cvId) });
    },
  });
}

export function useSendCvHandoff(cvId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CvHandoffSendInput) => sendCvHandoff(cvId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({ queryKey: cvKey(cvId) });
    },
  });
}

export function useCvHandoffInvite(
  token: string | undefined,
  enabled = true,
) {
  return useQuery<CvHandoffInvitePreview>({
    queryKey: ["cv-handoff", "invite", token],
    queryFn: () => fetchCvHandoffInvite(token!),
    enabled: enabled && !!token,
    // The guard errors (expired / wrong email / own CV) are terminal; retries
    // only delay the error screen.
    retry: false,
    staleTime: 30_000,
  });
}

export function useClaimCvHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { token: string; intent?: CvHandoffClaimIntent }) =>
      claimCvHandoff(input.token, input.intent),
    onSuccess: (result, input) => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({ queryKey: cvKey(result.cv.id) });
      queryClient.invalidateQueries({ queryKey: resolvedKey(result.cv.id) });
      if (input.intent === "update") {
        // 'update' also replaced the master profile server-side: refresh the
        // same queries the resume-import replace path invalidates
        // (CVResumeImportScreen.doSave; ['cvs'] is already covered above).
        void invalidateCvDocumentQueries(queryClient);
        queryClient.invalidateQueries({
          queryKey: ["onboarding", "resume-source"],
        });
      }
    },
  });
}

export function useCancelCvHandoff() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: cancelCvHandoff,
    onSuccess: (handoff) => {
      queryClient.invalidateQueries({ queryKey: CVS_KEY });
      queryClient.invalidateQueries({ queryKey: cvKey(handoff.sourceCvId) });
    },
  });
}
